import { Injectable } from '@angular/core';
import { of as observableOf, Observable, of } from 'rxjs';
import { Store } from '@ngrx/store';
import * as I18nAction from '@app/core/i18n/store/actions';
import { LocaleService } from '@app/core/i18n';
import { ContentRepositoryService } from '@app/core/services/content/content-repository.service';
import { ICountries, ICountry } from '@app/interfaces/country.interface';
import { map, take } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class I18nData {
  public oldCountries: any = {};
  public currencies: any = {};
  public languages: any = {};
  public countriesFiltered = {};

  constructor(
    private localeService: LocaleService,
    private contentRepository: ContentRepositoryService,
    private store: Store<any>
  ) {}

  load(): any {
    return this.contentRepository
      .getI18n()
      .pipe(take(1))
      .subscribe(
        res => {
          // config countries
          this.oldCountries.countriesShortLists = res.country.shortLists;
          this.oldCountries.countriesFullLists = res.country.fullList;
          this.oldCountries.title = res.country.title;
          this.getCurrentCountry();
          // config languages
          this.languages = res.language;
          this.getCurrentLanguage();

          // config currencies
          this.currencies = res.currency;
          this.getCurrentCurrency();
        },
        () => {
          /* Silently catch exception */
        }
      );
  }

  getCountries(): Observable<any> {
    return observableOf(this.oldCountries);
  }

  getFullListCountries(): Observable<any> {
    return observableOf(this.oldCountries.countriesFullLists);
  }

  getCurrentCountry() {
    const currentCountryCode = this.localeService.getCountry();

    const currentCountry = [...this.oldCountries.countriesShortLists, ...this.oldCountries.countriesFullLists].filter(
      country => country.isoCode === currentCountryCode
    )[0];

    this.store.dispatch(new I18nAction.UpdateCountry(currentCountry));
  }

  getCountryFromIso(iso: string): Observable<ICountry> {
    if (this.countriesFiltered[iso] !== undefined) {
      return of(this.countriesFiltered[iso]);
    } else {
      return this.getCountriesList().pipe(
        map(countries => {
          return countries[iso];
        })
      );
    }
  }

  getCountriesList(): Observable<ICountries> {
    return this.getFullListCountries().pipe(
      map(countries => {
        return Object.assign(
          this.countriesFiltered,
          ...countries.map(country => ({
            [country.isoCode]: {
              name: country.name,
              iso: country.isoCode
            }
          }))
        );
      })
    );
  }

  getLanguages(): Observable<any> {
    return observableOf(this.languages);
  }

  getCurrentLanguage() {
    const currentLanguageCode = this.localeService.getLocale().toString();

    const currentLang = this.languages.list.filter(lang => lang.isoCode === currentLanguageCode)[0];

    this.store.dispatch(new I18nAction.UpdateLanguage(currentLang));
  }

  getCurrencies(): Observable<any> {
    return observableOf(this.currencies);
  }

  getCurrentCurrency() {
    const currentCurrencyCode = this.localeService.getCurrency();

    const currentCurrency = this.currencies.list.filter(cur => cur.isoCode === currentCurrencyCode)[0];

    this.store.dispatch(new I18nAction.UpdateCurrency(currentCurrency));
  }
}
